import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, isRef as _isRef, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "dialog__header flex align-items-center" }
const _hoisted_2 = {
  class: "upload-wrapper flex relative text-center align-items-center justify-content-center w-full",
  style: { height: '98px' }
}
const _hoisted_3 = { class: "acceptable-files-text" }
const _hoisted_4 = { class: "upload-wrapper__file-list flex w-full left-0 align-items-center" }
const _hoisted_5 = { class: "field w-full message-field mt-4" }

import { computed, ref } from 'vue';
import FileUpload from 'primevue/fileupload';
import { storeToRefs } from 'pinia';
import PreviewMessageFile from '@/components/MessageCenter/components/Chat/components/PreviewMessageFile.vue';
import PreviewFileModal from '@/components/MessageCenter/components/Chat/components/PreviewFileModal.vue';
import Dialog from 'primevue/dialog';
import useFileList from '@/composables/useFileList';
import { EMIT } from '@/enum';
import { FileInterface, FileTypes } from '@/types';
import { useMessageCenterStore } from '@/store';


export default /*@__PURE__*/_defineComponent({
  __name: 'AddFileModal',
  setup(__props, { emit: __emit }) {

const emit = __emit;

const messageCenterStore = useMessageCenterStore();
const { getFileConfigurations, getFileTypes, messageText } = storeToRefs(messageCenterStore);
const { files, addFiles, removeFile } = useFileList();
const modalVisible = ref<boolean>(true);
const previewBlob = ref<Blob | File | undefined>(undefined);
const previewVisible = ref<boolean>(false);

const allowedFiles = computed(() => {
  const types = (getFileTypes.value ?? []) as FileTypes[];
  const exts = types
    .map((t) => (typeof t?.ext === 'string' ? t.ext.trim() : ''))
    .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`));
  return Array.from(new Set(exts)).join(', ');
});

const maxFileSizeMB = computed<number>(() => {
  return getFileConfigurations.value?.max_upload_size || 1024;
});

const maxFileSizeBytes = computed<number>(() => maxFileSizeMB.value * 1024 * 1024);

const maxFilesAllowed = computed<number>(() => {
  return getFileConfigurations.value?.max_files || 1;
});

const sendMessageFile = async (): Promise<void> => {
  if (!files.value.length) {
    messageCenterStore.doToast('Please select at least one file to send.', 'Error', true);
    return;
  }

  const formData = new FormData();
  formData.append('content', (messageText.value ?? '').toString());
  formData.append('type', 'file');

  const localFiles = [...(files.value as FileInterface[])];
  for (const file of localFiles) {
    if (file?.file instanceof Blob && typeof file.name === 'string' && file.name) {
      formData.append('attachment[]', file.file as Blob, file.name);
    }
  }

  if (formData.getAll('attachment[]').length === 0) {
    messageCenterStore.doToast('Please select at least one valid file to send.', 'Error', true);
    removeFiles(localFiles);
    modalVisible.value = false;
    emit(EMIT.CANCEL);
    return;
  }

  emit(EMIT.SEND_MESSAGE_FILE, formData);

  removeFiles(localFiles);
  messageText.value = '';
  previewVisible.value = false;
  previewBlob.value = undefined;
  modalVisible.value = false;
};

const onDrop = (e: { files: File[] }) => {
  if (e.files.length > maxFilesAllowed.value) {
    messageCenterStore.doToast(`You can only upload a maximum of ${maxFilesAllowed.value} files.`, 'Error', true);
    return;
  }

  addFiles(e.files);
};

const formDisabled = computed(() => {
  const text = (messageText.value ?? '').trim();
  return text.length === 0 && files.value.length === 0;
});

const cancel = () => {
  messageCenterStore.messageText = '';
  if (files.value.length > 0) removeFiles([...files.value]);

  emit(EMIT.CANCEL);
  modalVisible.value = false;
};

const removeFiles = (files: FileInterface[]) => {
  files.forEach((file) => removeFile(file));
};

const onShowPreview = (file: Blob | File) => {
  previewBlob.value = file;
  previewVisible.value = true;
};

const onCancelPreview = () => {
  previewVisible.value = false;
  previewBlob.value = undefined;
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Dialog), {
      visible: modalVisible.value,
      "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((modalVisible).value = $event)),
      class: "dialog__container add-document__modal",
      modal: true,
      draggable: false,
      onHide: cancel
    }, {
      header: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _cache[4] || (_cache[4] = _createElementVNode("h2", {
            class: "m-0",
            textContent: 'Add File'
          }, null, -1)),
          _withDirectives(_createVNode(_component_Icon, {
            name: "info",
            class: "ml-2 text-gray-500 text-s"
          }, null, 512), [
            [_directive_tooltip, `Supported file types: ${allowedFiles.value}`]
          ])
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_component_AppButton, {
          type: "secondary",
          text: "Cancel",
          onClick: cancel
        }),
        _createVNode(_component_AppButton, {
          type: "primary",
          text: "Send",
          disabled: formDisabled.value,
          onClick: sendMessageFile
        }, null, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(FileUpload), {
            "custom-upload": true,
            class: "upload relative",
            "choose-label": "Choose file",
            "invalid-file-type-message": 'Sorry, this file type is not supported. Please select a supported file type: {1}',
            auto: true,
            mode: "advanced",
            accept: allowedFiles.value || '',
            "show-cancel-button": false,
            "show-upload-button": false,
            "max-file-size": maxFileSizeBytes.value,
            multiple: true,
            "invalid-file-size-message": "Error: This file exceeds the max file size allowed.",
            "invalid-file-limit-message": "Error: You can only upload a maximum of {0} files.",
            onUploader: onDrop
          }, {
            empty: _withCtx(() => [
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "uploader-text font-bold text-base mt-2" }, [
                _createElementVNode("span", { class: "underline cursor-pointer" }, "Choose File"),
                _createTextVNode(" or drag and drop here ")
              ], -1)),
              _createElementVNode("p", _hoisted_3, "Max File size: " + _toDisplayString(maxFileSizeMB.value) + "MB", 1)
            ]),
            _: 1
          }, 8, ["accept", "max-file-size"])
        ]),
        (_unref(files).length > 0)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "upload-wrapper flex relative text-center align-items-center justify-content-center w-full",
              style: _normalizeStyle(!_unref(files).length ? { height: '98px' } : {}),
              onDragover: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
              onDrop: _cache[1] || (_cache[1] = _withModifiers(
        (event) => {
          event.preventDefault();
        }
      , ["prevent"]))
            }, [
              _createElementVNode("ul", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(files), (file) => {
                  return (_openBlock(), _createBlock(PreviewMessageFile, {
                    key: file.id,
                    class: "w-full flex text-left align-items-center",
                    file: file,
                    tag: "li",
                    onShowPreview: onShowPreview,
                    onRemove: _unref(removeFile)
                  }, null, 8, ["file", "onRemove"]))
                }), 128))
              ])
            ], 36))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _cache[6] || (_cache[6] = _createElementVNode("label", { for: "message" }, "Message", -1)),
          _createVNode(_component_InputText, {
            id: "message",
            modelValue: _unref(messageText),
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(messageText) ? (messageText).value = $event : null)),
            class: "message-field__input w-full",
            name: "message",
            onKeyup: _withKeys(sendMessageFile, ["enter"])
          }, null, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["visible"]),
    _createVNode(PreviewFileModal, {
      visible: previewVisible.value,
      title: 'Preview File',
      file: previewBlob.value,
      onCancel: onCancelPreview
    }, null, 8, ["visible", "file"])
  ], 64))
}
}

})